import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { BadStatusError } from '@confluence/network';

import type {
	EditorStartHeartbeatMutation as EditorStartHeartbeatMutationData,
	EditorStartHeartbeatMutationVariables,
	EditorStartHeartbeatMutation_experimentalEditorStartHeartbeat as ExperimentalHeartbeatResponse,
} from './__types__/EditorStartHeartbeatMutation.ts';

const TENANT_SUSPENDED_RESPONSE = 'Site temporarily unavailable';

export const useExperimentalEditorStartHeartbeatMutation = ({
	variables,
}: {
	variables: EditorStartHeartbeatMutationVariables;
}) => {
	return useExperimentalMutation<
		EditorStartHeartbeatMutationData,
		EditorStartHeartbeatMutationVariables,
		ExperimentalHeartbeatResponse
	>({
		url: '/wiki/api/v2/editor/heartbeat/start',
		method: 'POST',
		headers: {
			'Content-Type': MediaType.JSON,
			Accept: MediaType.JSON,
		},
		variables,
		body: ({ contentId, contentType, collabService, heartbeatUserType }) =>
			JSON.stringify({
				dataType: 'json',
				contentId,
				draftType: contentType ?? '',
				collabService,
				// confluence-frontend only sends heartbeat for Fabric editor, legacy editor heartbeat code is in confluence web resource.
				// heartbeat response is different for each editor version; only fabric heartbeat response in handled in this experimental mutation
				editorVersion: 'v2',
				heartbeatUserType,
			}),
		parseResponseOnError: async (fetchError) => {
			// this catches unhandled server errors (e.g. internal server error)
			let error: string;
			let message: string;

			if (fetchError instanceof BadStatusError) {
				try {
					// try to parse original error message
					const errorMessageJson = await fetchError.response.json();

					if (errorMessageJson?.errorMessage === TENANT_SUSPENDED_RESPONSE) {
						// error from atlassian-proxy during region migration
						error = 'tenant-unavailable';
						message = 'Site temporarily unavailable';
					} else {
						// Parse heartbeat REST error response
						error = errorMessageJson?.errors[0].code;
						message = errorMessageJson?.errors[0].title;
					}
				} catch {
					error = fetchError.response.statusText;
					message = fetchError.message;
				}
			} else {
				error = fetchError.name;
				message = fetchError.message;
			}

			return {
				orderedUsers: null,
				editorSessionId: null,
				error,
				message,
				subtype: null,
			};
		},
		parseResult: ({ orderedUsers, editorSessionId, subtype, error, message }) => {
			return {
				experimentalEditorStartHeartbeat: {
					orderedUsers,
					editorSessionId,
					error,
					message,
					subtype,
				},
			};
		},
	});
};
